import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { api } from "../../lib/api";
import { cacheGet, cacheSet } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { Spinner } from "../../components/ui";
import { CategoryCard, ServiceCard } from "../../components/marketplace";

export default function CustomerServices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState(() => cacheGet("catalog-categories").data || []);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(() => categories.length === 0);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const fetchServices = useCallback(async () => {
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (search) params.search = search;
      if (minPrice) params.min_price = parseInt(minPrice);
      if (maxPrice) params.max_price = parseInt(maxPrice);
      if (sort) params.sort = sort;

      const [catRes, svcRes] = await Promise.all([
        api.get("/api/categories"),
        api.get("/api/services", { params }),
      ]);
      setCategories(catRes.data);
      cacheSet("catalog-categories", catRes.data, 300000);
      setServices(svcRes.data);
      // Cache hasil list per kombinasi filter (TTL pendek, pencarian volatil).
      cacheSet(`svc-list:${JSON.stringify(params)}`, svcRes.data, 30000);
    } catch (err) {
      console.error("Failed to load services:", err);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, search, minPrice, maxPrice, sort]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Autocomplete: debounce 300ms
  useEffect(() => {
    if (!search.trim()) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/services/suggestions/list", {
          params: { q: search.trim() },
        });
        setSuggestions(data || []);
      } catch (_) {}
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Sinkron state dengan URL: Link kategori menavigasi ke route yang SAMA
  // (?category=) tanpa remount, jadi state harus ikut berubah bila URL berubah.
  useEffect(() => {
    setActiveCategory(searchParams.get("category") || "");
    setSearch(searchParams.get("search") || "");
  }, [searchParams]);

  const clearFilters = () => {
    setSearch("");
    setActiveCategory("");
    setMinPrice("");
    setMaxPrice("");
    setSort("");
    setSearchParams({});
  };

  const hasFilters = search || activeCategory || minPrice || maxPrice || sort;

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to="/customer" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-brand-800">Cari Jasa</h1>
            <p className="text-brand-400 mt-1">Temukan pekerja terbaik untuk kebutuhan Anda</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition ${
              showFilters ? "bg-brand-600 text-white border-brand-600" : "border-brand-200 text-brand-600 hover:bg-brand-50"
            }`}
            data-testid="filter-toggle"
          >
            <SlidersHorizontal className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            list="svc-suggestions"
            className="w-full rounded-2xl border-2 border-brand-100 bg-white px-4 py-3.5 pl-12 pr-12 text-brand-800 placeholder:text-brand-300 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100 transition"
            placeholder="Cari layanan..."
            data-testid="services-search"
            autoComplete="off"
          />
          <datalist id="svc-suggestions">
            {suggestions.map((s) => (
              <option key={s.slug} value={s.name} />
            ))}
          </datalist>
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-300 hover:text-brand-500"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-brand-500">Urutkan:</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl border-2 border-brand-100 bg-white px-3 py-2 text-sm text-brand-800 focus:border-brand-500 focus:outline-none"
            data-testid="services-sort"
          >
            <option value="">Paling relevan</option>
            <option value="price_asc">Harga terendah</option>
            <option value="price_desc">Harga tertinggi</option>
            <option value="popular">Paling banyak pekerja</option>
          </select>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="card p-4 space-y-3">
            <p className="font-bold text-brand-800 text-sm">Filter Harga</p>
            <div className="flex gap-3">
              <input
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="Min Rp"
                className="flex-1 rounded-xl border-2 border-brand-100 px-3 py-2 text-sm text-brand-800 focus:border-brand-500 focus:outline-none"
                data-testid="filter-min-price"
              />
              <input
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Max Rp"
                className="flex-1 rounded-xl border-2 border-brand-100 px-3 py-2 text-sm text-brand-800 focus:border-brand-500 focus:outline-none"
                data-testid="filter-max-price"
              />
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="text-sm text-brand-500 hover:text-brand-700 transition">
                Hapus semua filter
              </button>
            )}
          </div>
        )}

        {/* Active category chip */}
        {activeCategory && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-brand-500">Kategori:</span>
            <span className="chip bg-brand-600 text-white text-sm flex items-center gap-1.5">
              {categories.find((c) => c.slug === activeCategory)?.name || activeCategory}
              <button
                onClick={() => {
                  setActiveCategory("");
                  const next = Object.fromEntries(searchParams.entries());
                  delete next.category;
                  setSearchParams(next);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        )}

        {loading ? (
          <Spinner label="Memuat layanan..." />
        ) : (
          <>
            {/* Categories grid */}
            {!activeCategory && !search && (
              <div>
                <h2 className="text-brand-800">Kategori</h2>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {categories.map((cat) => (
                    <CategoryCard
                      key={cat.id}
                      category={cat}
                      to={`/customer/services?category=${cat.slug}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Services list */}
            {(activeCategory || search) && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-brand-800">
                    {activeCategory
                      ? categories.find((c) => c.slug === activeCategory)?.name
                      : `Hasil pencarian "${search}"`}
                  </h2>
                  <span className="text-sm text-brand-400">{services.length} layanan</span>
                </div>
                {services.length > 0 ? (
                  <div className="space-y-3">
                    {services.map((svc) => (
                      <ServiceCard
                        key={svc.id}
                        service={svc}
                        to={`/customer/services/${svc.slug}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="card p-8 text-center">
                    <p className="font-bold text-brand-800">Tidak ada layanan ditemukan</p>
                    <p className="text-brand-400 mt-1">Coba ubah filter atau kata kunci pencarian</p>
                    <button onClick={clearFilters} className="btn-primary mt-4">
                      Lihat semua layanan
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
